import { createPortal } from 'react-dom'
import { motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { playUpgradeSoundSequence } from '@/src/game/audio/uiSounds'

// Reihenfolge muss mit unlockedCount (1-4) übereinstimmen
const ITEM_NAMES = ['boots', 'gloves', 'helmet', 'vest'] as const
// Phasen der Sequenz
type Phase = 'flying' | 'bursting' | 'item-visible' | 'item-fading'

type SequenceData = {
    id: number
    startX: number
    startY: number
    itemImage: string
}

type Props = {
    bubbleRef: React.RefObject<HTMLImageElement | null>
}

export function UpgradeSequenceOverlay({ bubbleRef }: Props) {
    const [sequence, setSequence] = useState<SequenceData | null>(null)
    const [phase, setPhase] = useState<Phase>('flying')
    const idRef = useRef(0)
    const timeoutsRef = useRef<number[]>([])
    const isStele = useSteleBreakpoint();

    const clearTimeouts = () => {
        timeoutsRef.current.forEach(window.clearTimeout)
        timeoutsRef.current = []
    }

    useEffect(() => {
        const handleUnlock = (e: Event) => {
            const { unlockedCount } = (e as CustomEvent<{ unlockedCount: number }>).detail

            playUpgradeSoundSequence(unlockedCount)

            if (!bubbleRef.current) {
                return
            }

            const itemName = ITEM_NAMES[unlockedCount - 1]

            if (!itemName) {
                return
            }

            const rect = bubbleRef.current.getBoundingClientRect()

            clearTimeouts()

            setSequence({
                id: ++idRef.current,
                startX: rect.left + rect.width / 2,
                startY: rect.top + rect.height / 2,
                itemImage: `/images/editor/${itemName}.png`,
            })
            setPhase('flying')

            // Spiel einfrieren
            window.dispatchEvent(new Event('upgrade-sequence-start'))

            // Phasen-Timing
            timeoutsRef.current.push(window.setTimeout(() => setPhase('bursting'), 600))
            timeoutsRef.current.push(window.setTimeout(() => setPhase('item-visible'), 900))
            timeoutsRef.current.push(window.setTimeout(() => setPhase('item-fading'), 1600))
            timeoutsRef.current.push(window.setTimeout(() => {
                setSequence(null)
                window.dispatchEvent(new Event('upgrade-sequence-end'))
            }, 2200))
        }

        window.addEventListener('upgrade-unlock', handleUnlock)
        return () => {
            window.removeEventListener('upgrade-unlock', handleUnlock)
            clearTimeouts()
        }
    }, [bubbleRef])

    if (!sequence) return null

    const centerX = window.innerWidth / 2
    const centerY = window.innerHeight / 2
    const dx = centerX - sequence.startX
    const dy = centerY - sequence.startY

    return createPortal(
        <div className='z-[9999] fixed inset-0 pointer-events-none'>

            {/* Dunkler Hintergrund */}
            <motion.div
                className='absolute inset-0 bg-black'
                initial={{ opacity: 0 }}
                animate={{
                    opacity: phase === 'item-fading' ? 0 : 0.65,
                }}
                transition={{ duration: phase === 'item-fading' ? 0.6 : 0.3 }}
            />

            {/* Fliegende Bubble */}
            {(phase === 'flying' || phase === 'bursting') && (
                <motion.img
                    key={`bubble-${sequence.id}`}
                    src='/images/Bubble_Idle.png'
                    alt=''
                    style={{
                        position: 'fixed',
                        left: sequence.startX,
                        top: sequence.startY,
                        translateX: '-50%',
                        translateY: '-50%',
                        width: isStele ? 216 : 54,
                        height: isStele ? 216 : 54,
                    }}
                    initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
                    animate={phase === 'flying' ? {
                        x: dx,
                        y: dy,
                        scale: 3.5,
                        opacity: 1,
                    } : {
                        x: dx,
                        y: dy,
                        scale: 9,
                        opacity: 0,
                    }}
                    transition={phase === 'flying'
                        ? { duration: 0.55, ease: [0.4, 0, 0.2, 1] }
                        : { duration: 0.25, ease: 'easeOut' }
                    }
                />
            )}

            {/* Item — sichtbar in allen Phasen außer vor dem Fly */}
            {(phase === 'flying' || phase === 'bursting' || phase === 'item-visible' || phase === 'item-fading') && (
                <motion.img
                    key={`item-${sequence.id}`}
                    src={sequence.itemImage}
                    alt=''
                    style={{
                        position: 'fixed',
                        left: sequence.startX,
                        top: sequence.startY,
                        translateX: '-50%',
                        translateY: '-50%',
                        width: isStele ? 216 : 54,
                        height: isStele ? 216 : 54,
                        objectFit: 'contain',
                    }}
                    animate={
                        phase === 'flying' ? {
                            x: dx,
                            y: dy,
                            width: isStele ? 400 : 100,
                            height: isStele ? 400 : 100,
                            opacity: 1,
                        } : phase === 'bursting' ? {
                            x: dx,
                            y: dy,
                            width: isStele ? 400 : 100,
                            height: isStele ? 400 : 100,
                            opacity: 1,
                        } : phase === 'item-visible' ? {
                            x: dx,
                            y: dy,
                            width: isStele ? 560 : 140,
                            height: isStele ? 560 : 140,
                            opacity: 1,
                        } : {
                            x: dx,
                            width: isStele ? 560 : 140,
                            height: isStele ? 560 : 140,
                            opacity: 0,
                            scale: 0.1,
                            y: dy + (isStele ? 600 : 75),
                        }
                    }
                    transition={
                        phase === 'flying'
                            ? { duration: 0.55, ease: [0.4, 0, 0.2, 1] }
                            : phase === 'item-visible'
                                ? { duration: 0.2, ease: 'easeOut' }
                                : { duration: 0.55, ease: 'easeOut' }
                    }
                />
            )}

        </div>,
        document.body,
    )
}